import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, ProtectedRoute, AdminRoute, HomeRedirect, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import Attempts from "@/pages/Attempts";
import Clients from "@/pages/Clients";
import Servers from "@/pages/Servers";
import Affidavits from "@/pages/Affidavits";
import CourtForms from "@/pages/CourtForms";
import JobDetail from "@/pages/JobDetail";
import CourtCases from "@/pages/CourtCases";
import Settings from "@/pages/Settings";
import Pending from "@/pages/Pending";
import Reports from "@/pages/Reports";
import Today from "@/pages/Today";
import NewJob from "@/pages/NewJob";
import Tracker from "@/pages/Tracker";
import Portal from "@/pages/Portal";
import Intake from "@/pages/Intake";
import Home from "@/pages/site/Home";
import { ProcessService, Investigations, Records, Contact, TrackPage } from "@/pages/site/Pages";
import { Screening } from "@/pages/site/Screening";
import { Privacy, Terms } from "@/pages/site/Legal";
import County, { COUNTIES } from "@/pages/site/County";
import City, { CITIES } from "@/pages/site/City";
import SimplyServed from "@/pages/site/SimplyServed";
import Serve from "@/pages/Serve";
import Sign from "@/pages/Sign";
import Ack from "@/pages/Ack";
import Help from "@/pages/Help";
import AppShell from "@/components/AppShell";

const APP_HOST_RE = /(^|\.)delawareps\.com$|^localhost$/i;
const isAppHost = () => APP_HOST_RE.test(window.location.hostname) && !window.location.hostname.includes("preview");

const PUBLIC_PATHS = new Set(["/", "/home", "/process-service", "/background-screening", "/investigations", "/records", "/contact", "/track", "/simply-served"]);
const WIX_REDIRECTS = { "/contact-us": "/contact", "/delaware-process-service": "/process-service", "/process-server-kent-county": "/process-server/kent-county", "/process-server-sussex-county": "/process-server/sussex-county", "/process-server-new-castle-county": "/process-server/new-castle-county", "/delaware-background-check": "/background-screening", "/background-check": "/background-screening", "/background-screening-delaware": "/background-screening", "/asset-locates": "/investigations", "/record-retrieval": "/records", "/services": "/process-service", "/about": "/", "/about-us": "/" };
const PRIMARY_HOST = "delawareps.com";
const LEGACY_HOST_RE = /(^|\.)yourdelawarepi\.com$|^www\.delawareps\.com$/i;
if (typeof window !== "undefined" && LEGACY_HOST_RE.test(window.location.hostname)) {
  window.location.replace(`https://${PRIMARY_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`);
}

function AreaPage() {
  const { slug } = useParams();
  if (CITIES[slug]) return <City />;
  if (COUNTIES[slug]) return <County />;
  return <Navigate to="/process-service" replace />;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (isAppHost() && loading) return null;
  if (isAppHost() && user?.approved) return <Navigate to="/app" replace />;
  return <Home />;
}


function RobotsGuard({ pathname }) {
  React.useEffect(() => {
    if (PUBLIC_PATHS.has(pathname) || pathname.startsWith("/process-server/")) return;
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
    el.setAttribute("content", "noindex, nofollow");
    document.head.querySelector('link[rel="canonical"]')?.remove();
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: synchronous check of URL fragment before any Route/ProtectedRoute logic runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const wixPath = location.pathname.replace(/\/+$/, "").toLowerCase();
  if (WIX_REDIRECTS[wixPath]) return <Navigate to={WIX_REDIRECTS[wixPath]} replace />;
  return (
    <>
    <RobotsGuard pathname={location.pathname} />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pending" element={<Pending />} />
      <Route path="/track/:token" element={<Tracker />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/intake/:token" element={<Intake />} />
      <Route path="/" element={<RootRoute />} />
      <Route path="/inforetrieval" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/simply-served" element={<SimplyServed />} />
      <Route path="/serve/:token" element={<Serve />} />
      <Route path="/sign/:token" element={<Sign />} />
      <Route path="/ack/:token" element={<Ack />} />
      <Route path="/process-service" element={<ProcessService />} />
      <Route path="/background-screening" element={<Screening />} />
      <Route path="/investigations" element={<Investigations />} />
      <Route path="/records" element={<Records />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/track" element={<TrackPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/process-server/:slug" element={<AreaPage />} />
      <Route path="/app" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<AdminRoute><AppShell><Dashboard /></AppShell></AdminRoute>} />
      <Route path="/today" element={<ProtectedRoute><AppShell><Today /></AppShell></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><AppShell><Jobs /></AppShell></ProtectedRoute>} />
      <Route path="/jobs/new" element={<AdminRoute><AppShell><NewJob /></AppShell></AdminRoute>} />
      <Route path="/jobs/:id/edit" element={<AdminRoute><AppShell><NewJob /></AppShell></AdminRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute><AppShell><JobDetail /></AppShell></ProtectedRoute>} />
      <Route path="/cases" element={<AdminRoute><AppShell><CourtCases /></AppShell></AdminRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppShell><Settings /></AppShell></ProtectedRoute>} />
      <Route path="/help" element={<AdminRoute><AppShell><Help /></AppShell></AdminRoute>} />
      <Route path="/reports" element={<AdminRoute><AppShell><Reports /></AppShell></AdminRoute>} />
      <Route path="/attempts" element={<AdminRoute><AppShell><Attempts /></AppShell></AdminRoute>} />
      <Route path="/clients" element={<AdminRoute><AppShell><Clients /></AppShell></AdminRoute>} />
      <Route path="/servers" element={<AdminRoute><AppShell><Servers /></AppShell></AdminRoute>} />
      <Route path="/affidavits" element={<AdminRoute><AppShell><Affidavits /></AppShell></AdminRoute>} />
      <Route path="/forms" element={<AdminRoute><AppShell><CourtForms /></AppShell></AdminRoute>} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
